/**
 * AR Aging Pie Chart Component
 * Shows distribution of outstanding amounts by aging bucket
 */

import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { Box, Typography, useTheme } from "@mui/material";
import type { ARAgingBucket } from "@/types/report";
import { formatCompactCurrency } from "@/utils/formatters";
import { toNumber } from "@/utils/money";

interface AgingPieChartProps {
  buckets: ARAgingBucket[];
  height?: number;
  showLegend?: boolean;
  title?: string;
}

const formatCurrency = (value: number): string => formatCompactCurrency(value);

interface AgingChartPayload {
  name: string;
  value: number;
  count: number;
  percentage: string;
}

interface TooltipPayloadItem {
  payload: AgingChartPayload;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: "background.paper",
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" gutterBottom sx={{
          fontWeight: 600
        }}>
          {data.name}
        </Typography>
        <Typography variant="body2">
          Amount: {formatCurrency(data.value)}
        </Typography>
        <Typography variant="body2">
          Invoices: {data.count}
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {data.percentage}% of total
        </Typography>
      </Box>
    );
  }
  return null;
};

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
}) => {
  if (!cx || !cy || !midAngle || !innerRadius || !outerRadius || !percent || percent < 0.05) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const AgingPieChart: React.FC<AgingPieChartProps> = ({
  buckets,
  height = 300,
  showLegend = true,
  title,
}) => {
  const theme = useTheme();

  const total = buckets.reduce((sum, b) => sum + toNumber(b.total_amount), 0);

  const chartData = buckets.map((bucket) => ({
    name: bucket.bucket_name,
    value: bucket.total_amount,
    count: bucket.invoice_count,
    percentage: total > 0 ? ((bucket.total_amount / total) * 100).toFixed(1) : "0",
  }));

  // Colors from green (current) to red (90+ days)
  const COLORS = [
    theme.palette.success.main,   // Current
    theme.palette.success.light,  // 1-30 days
    theme.palette.warning.main,   // 31-60 days
    theme.palette.warning.dark,   // 61-90 days
    theme.palette.error.main,     // 90+ days
  ];

  const summaryText = chartData
    .filter((d) => d.value > 0)
    .map((d) => `${d.name}: ${formatCurrency(d.value)} (${d.percentage}%)`)
    .join(", ");

  return (
    <Box role="figure" aria-label={title || "AR Aging Distribution"}>
      {title && (
        <Typography variant="h6" gutterBottom sx={{
          fontWeight: 600
        }}>
          {title}
        </Typography>
      )}
      <Box className="sr-only" aria-live="polite">
        {summaryText || "No aging data available"}
      </Box>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={height / 3}
            innerRadius={height / 6}
            fill={theme.palette.chart.categorical[0]}
            dataKey="value"
            paddingAngle={2}
          >
            {chartData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              formatter={(value: string) => (
                <span style={{ color: theme.palette.text.primary, fontSize: 12 }}>
                  {value}
                </span>
              )}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default AgingPieChart;
